import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubmissions, getSubmissionStats, callApproveWidget } from '../../services/submissionService';
import { getWidgetByTitle, getWidgetById, getWidgets, uploadWidgetImage, updateWidget } from '../../services/widgetService';

const DEVICE_OPTIONS = ['web', 'ios', 'android', 'others'];
const TAG_OPTIONS = ['analysis', 'play', 'learn', 'openings', 'people', 'books', 'news', 'watch', 'community', 'podcasts'];

const SubmissionReview = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [editOverrides, setEditOverrides] = useState({});
  const [linksEntries, setLinksEntries] = useState([{ key: '', value: '' }]);
  const [imageUploading, setImageUploading] = useState(false);
  const [matchedWidget, setMatchedWidget] = useState(null); // live widget from widgets collection
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [slugMap, setSlugMap] = useState({}); // title → slug lookup for View links

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [subs, st, allWidgets] = await Promise.all([
        getSubmissions(filter),
        getSubmissionStats(),
        getWidgets()
      ]);
      setSubmissions(subs || []);
      setStats(st);
      const map = {};
      (allWidgets || []).forEach(w => { if (w.slug && w.title) map[w.title] = w.slug; });
      setSlugMap(map);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      if (err.code === 'permission-denied' || err.message?.includes('Missing or insufficient permissions')) {
        setSubmissions([]);
        setError(null);
      } else {
        setError('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setEditOverrides({});
      setLinksEntries([{ key: '', value: '' }]);
      setReviewNotes('');
      setMatchedWidget(null);
      return;
    }

    const sub = submissions.find(s => s.id === id);
    setExpandedId(id);
    setMatchedWidget(null);
    setReviewNotes('');

    // Try to find the matching live widget — use existingWidgetId if available, else title lookup
    setWidgetLoading(true);
    try {
      const liveWidget = sub.existingWidgetId
        ? await getWidgetById(sub.existingWidgetId)
        : await getWidgetByTitle(sub.title);
      setMatchedWidget(liveWidget);

      // Preload from live widget if it exists, otherwise from submission
      const src = liveWidget || sub;
      setEditOverrides({
        title: src.title || '',
        link: src.link || '',
        subtitle: src.subtitle || '',
        description: src.description || '',
        icon: src.icon || '',
        slug: src.slug || slugify(sub.title || ''),
        tagMulti: Array.isArray(src.tag) ? src.tag : (src.tag ? [src.tag] : []),
        devices: Array.isArray(src.devices) ? src.devices : [],
        indie: !!src.indie,
        hidden: !!src.hidden,
        verified: !!src.verified,
        isNew: !!src.isNew,
        isActive: !!src.isActive,
        date: src.date || '',
        longDescription: src.longDescription || '',
        featuresInput: Array.isArray(src.features) ? src.features.join('\n') : '',
        comments: src.comments || '',
        excludeFromAll: !!src.excludeFromAll,
        titled: !!src.titled,
        chessTitle: src.chessTitle || '',
      });

      // Populate links
      const linksObj = src.links;
      if (linksObj && typeof linksObj === 'object' && Object.keys(linksObj).length) {
        setLinksEntries(Object.entries(linksObj).map(([key, val]) => ({
          key,
          value: Array.isArray(val) ? val.join(', ') : String(val)
        })));
      } else {
        setLinksEntries([{ key: '', value: '' }]);
      }
    } catch (err) {
      console.warn('Could not fetch matching widget:', err);
      // Fall back to submission data
      setEditOverrides({
        title: sub.title || '',
        link: sub.link || '',
        subtitle: sub.subtitle || '',
        description: sub.description || '',
        icon: sub.icon || '',
        slug: slugify(sub.title || ''),
        tagMulti: Array.isArray(sub.tag) ? sub.tag : (sub.tag ? [sub.tag] : []),
        devices: Array.isArray(sub.devices) ? sub.devices : [],
        indie: false, hidden: false, verified: false, isNew: true, isActive: false,
        date: '', longDescription: '', featuresInput: '', comments: '', excludeFromAll: false,
        titled: false, chessTitle: '',
      });
      setLinksEntries([{ key: '', value: '' }]);
    } finally {
      setWidgetLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setImageUploading(true);
      const imageUrl = await uploadWidgetImage(file);
      setEditOverrides(p => ({ ...p, icon: imageUrl }));
    } catch (err) {
      console.error('Error uploading image:', err);
      alert(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleQuickApprove = async (submissionId) => {
    try {
      setActionLoading(submissionId);
      const sub = submissions.find(s => s.id === submissionId);
      await callApproveWidget(submissionId, 'reviewed', '', {}, sub?.existingWidgetId || null);
      await fetchData();
    } catch (err) {
      console.error('Quick approve error:', err);
      alert(`Failed: ${err?.message || err?.details || 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAction = async (submissionId, action) => {
    try {
      setActionLoading(submissionId);

      if (action === 'reject') {
        if (!window.confirm(`Remove "${submissions.find(s => s.id === submissionId)?.title}" from the site? This deletes the live widget.`)) return;
        await callApproveWidget(submissionId, 'reject', reviewNotes, {}, matchedWidget?.id || null);
      } else {
        // "reviewed" — save edits to existing widget (or create if none), mark submission as reviewed
        const overrides = {};

        // Always send all fields so the widget is fully populated
        overrides.title = editOverrides.title || '';
        overrides.link = editOverrides.link || '';
        overrides.subtitle = editOverrides.subtitle || '';
        overrides.description = editOverrides.description || '';
        overrides.icon = editOverrides.icon || '';
        overrides.slug = editOverrides.slug || '';
        overrides.longDescription = editOverrides.longDescription || '';
        overrides.comments = editOverrides.comments || '';
        overrides.date = editOverrides.date || '';
        overrides.indie = !!editOverrides.indie;
        overrides.hidden = !!editOverrides.hidden;
        overrides.verified = !!editOverrides.verified;
        overrides.isNew = !!editOverrides.isNew;
        overrides.isActive = !!editOverrides.isActive;
        overrides.excludeFromAll = !!editOverrides.excludeFromAll;
        overrides.titled = !!editOverrides.titled;
        overrides.chessTitle = editOverrides.chessTitle || '';
        overrides.tag = editOverrides.tagMulti?.length ? editOverrides.tagMulti : '';
        overrides.devices = editOverrides.devices?.length ? editOverrides.devices : [];
        overrides.features = editOverrides.featuresInput
          ? editOverrides.featuresInput.split('\n').map(f => f.trim()).filter(Boolean)
          : [];

        const linksObj = linksEntries.reduce((acc, { key, value }) => {
          const k = (key || '').trim();
          const v = (value || '').trim();
          if (k && v) acc[k] = v;
          return acc;
        }, {});
        overrides.links = Object.keys(linksObj).length ? linksObj : {};

        await callApproveWidget(
          submissionId,
          'reviewed',
          reviewNotes,
          overrides,
          matchedWidget?.id || null // pass existing widget ID
        );
      }

      setExpandedId(null);
      setReviewNotes('');
      setEditOverrides({});
      setLinksEntries([{ key: '', value: '' }]);
      setMatchedWidget(null);
      await fetchData();
    } catch (err) {
      console.error(`Error processing submission:`, err);
      const msg = err?.message || err?.details || '';
      alert(`Failed to process submission: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-900 text-yellow-200',
    approved: 'bg-green-900 text-green-200',
    rejected: 'bg-red-900 text-red-200',
  };

  const inputCls = 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
  const labelCls = 'block text-xs text-gray-400 mb-1 font-medium';
  const checkboxCls = 'w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!embedded && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-100">Submissions</h2>
            <p className="text-gray-400 mt-2">Review new and updated widgets from the discovery pipeline</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
          >
            ← Admin Dashboard
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Total</h3>
          <p className="text-3xl font-bold text-blue-400">{stats.total}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Unreviewed</h3>
          <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Reviewed</h3>
          <p className="text-3xl font-bold text-green-400">{stats.approved}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Rejected</h3>
          <p className="text-3xl font-bold text-red-400">{stats.rejected}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'pending', label: 'Unreviewed' },
          { key: 'approved', label: 'Reviewed' },
          { key: 'rejected', label: 'Rejected' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-gray-300 border border-gray-700'
            }`}
          >
            {label} ({stats[key] || 0})
          </button>
        ))}
      </div>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="text-xl font-semibold text-gray-300 mb-4">No {filter === 'pending' ? 'unreviewed' : filter} submissions</h3>
          <p className="text-gray-400">Nothing to show here yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map(sub => (
            <div key={sub.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              {/* Summary Row */}
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-750 transition-colors"
                onClick={() => handleExpand(sub.id)}
              >
                {sub.icon && (
                  <img src={sub.icon} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-gray-100 font-medium truncate">{sub.title}</h4>
                  <p className="text-gray-400 text-sm truncate">{sub.link}</p>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[sub.status]}`}>
                  {sub.status === 'pending' ? (sub.type === 'enrichment' ? 'updated' : 'new') : sub.status === 'approved' ? 'reviewed' : sub.status}
                </span>
                <div className="text-gray-400 text-sm flex-shrink-0">
                  {sub.source === 'ai_agent' ? 'AI' : sub.source === 'api' ? 'API' : 'Form'}
                </div>
                <div className="text-gray-500 text-sm flex-shrink-0">
                  {formatDate(sub.submittedAt)}
                </div>
                {sub.status === 'pending' && (
                  <div className="flex gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <a
                      href={`/${slugMap[sub.title] || slugify(sub.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 text-xs font-medium px-2 py-1 rounded border border-blue-500/30 hover:border-blue-400/50 transition-colors"
                    >
                      View
                    </a>
                    <button
                      onClick={() => handleQuickApprove(sub.id)}
                      disabled={actionLoading === sub.id}
                      className="text-green-400 hover:text-green-300 text-xs font-medium px-2 py-1 rounded border border-green-500/30 hover:border-green-400/50 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === sub.id ? '...' : '✓'}
                    </button>
                    <button
                      onClick={() => handleAction(sub.id, 'reject')}
                      disabled={actionLoading === sub.id}
                      className="text-red-400 hover:text-red-300 text-xs font-medium px-2 py-1 rounded border border-red-500/30 hover:border-red-400/50 transition-colors disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <span className="text-gray-500">{expandedId === sub.id ? '▲' : '▼'}</span>
              </div>

              {/* Expanded Full Editor */}
              {expandedId === sub.id && (
                <div className="border-t border-gray-700 p-5 space-y-5">
                  {/* Status banner */}
                  {matchedWidget && (
                    <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded-lg text-sm">
                      Live widget found — editing <strong>{matchedWidget.title}</strong> ({matchedWidget.id})
                      {matchedWidget.slug && <span className="text-blue-400 ml-2">trendingchess.com/{matchedWidget.slug}</span>}
                    </div>
                  )}
                  {!matchedWidget && !widgetLoading && sub.status === 'pending' && (
                    <div className="bg-yellow-900/30 border border-yellow-700 text-yellow-200 px-4 py-2 rounded-lg text-sm">
                      No matching live widget found — approving will create a new one
                    </div>
                  )}
                  {widgetLoading && (
                    <div className="text-gray-400 text-sm flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                      Loading widget data...
                    </div>
                  )}

                  {/* Submission meta */}
                  <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-700">
                    <div>
                      <span className="text-xs text-gray-500">Submitter</span>
                      <p className="text-gray-300 text-sm">{sub.submitterName || 'AI Discovery Agent'}{sub.submitterEmail && ` (${sub.submitterEmail})`}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Source</span>
                      <p className="text-gray-300 text-sm">{sub.source}</p>
                    </div>
                  </div>

                  {sub.status === 'pending' ? (
                    <>
                      {/* ── Row 1: Title + Slug ── */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>Title *</label>
                          <input
                            type="text"
                            value={editOverrides.title || ''}
                            onChange={e => {
                              const title = e.target.value;
                              setEditOverrides(p => ({
                                ...p,
                                title,
                                slug: p.slug === slugify(p.title || '') ? slugify(title) : p.slug,
                              }));
                            }}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Slug</label>
                          <input
                            type="text"
                            value={editOverrides.slug || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, slug: e.target.value }))}
                            className={inputCls}
                          />
                          {editOverrides.slug && (
                            <p className="text-gray-500 text-xs mt-1">trendingchess.com/{editOverrides.slug}</p>
                          )}
                        </div>
                      </div>

                      {/* ── Row 2: Subtitle + Link ── */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>Subtitle</label>
                          <input
                            type="text"
                            value={editOverrides.subtitle || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, subtitle: e.target.value }))}
                            className={inputCls}
                            placeholder="Max 3 words"
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Main Link *</label>
                          <input
                            type="text"
                            value={editOverrides.link || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, link: e.target.value }))}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      {/* ── Icon ── */}
                      <div>
                        <label className={labelCls}>Icon *</label>
                        {editOverrides.icon && (
                          <div className="relative mb-3">
                            <img
                              src={editOverrides.icon}
                              alt="Icon"
                              className="w-full h-40 object-cover rounded-lg"
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                            <button
                              type="button"
                              onClick={() => setEditOverrides(p => ({ ...p, icon: '' }))}
                              className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full text-xs"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <div className="flex gap-3">
                          <input type="file" accept="image/*" onChange={handleImageUpload} disabled={imageUploading} className="hidden" id={`img-upload-${sub.id}`} />
                          <label
                            htmlFor={`img-upload-${sub.id}`}
                            className={`bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm cursor-pointer ${imageUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {imageUploading ? 'Uploading...' : 'Upload Image'}
                          </label>
                          <input
                            type="url"
                            value={editOverrides.icon || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, icon: e.target.value }))}
                            className={`flex-1 ${inputCls}`}
                            placeholder="Or paste image URL"
                          />
                        </div>
                      </div>

                      {/* ── Tags + Devices ── */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>Tags</label>
                          <div className="flex flex-wrap gap-2 bg-gray-700 border border-gray-600 rounded-lg p-3">
                            {TAG_OPTIONS.map(opt => (
                              <label key={opt} className="flex items-center gap-1.5 text-gray-200 text-xs">
                                <input
                                  type="checkbox"
                                  checked={(editOverrides.tagMulti || []).includes(opt)}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setEditOverrides(p => ({
                                      ...p,
                                      tagMulti: checked
                                        ? [...new Set([...(p.tagMulti || []), opt])]
                                        : (p.tagMulti || []).filter(v => v !== opt)
                                    }));
                                  }}
                                  className={checkboxCls}
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className={labelCls}>Devices</label>
                          <div className="flex flex-wrap gap-3 bg-gray-700 border border-gray-600 rounded-lg p-3">
                            {DEVICE_OPTIONS.map(opt => (
                              <label key={opt} className="flex items-center gap-1.5 text-gray-200 text-xs">
                                <input
                                  type="checkbox"
                                  checked={(editOverrides.devices || []).includes(opt)}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setEditOverrides(p => ({
                                      ...p,
                                      devices: checked
                                        ? [...new Set([...(p.devices || []), opt])]
                                        : (p.devices || []).filter(v => v !== opt)
                                    }));
                                  }}
                                  className={checkboxCls}
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* ── Status Toggles ── */}
                      <div className="flex flex-wrap gap-6">
                        {[
                          { key: 'indie', label: 'Indie' },
                          { key: 'hidden', label: 'Hidden' },
                          { key: 'verified', label: 'Verified' },
                          { key: 'isNew', label: 'New' },
                          { key: 'isActive', label: 'Active' },
                          { key: 'excludeFromAll', label: 'Exclude from All' },
                          { key: 'titled', label: 'Titled' },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-2 text-gray-300 text-sm">
                            <input
                              type="checkbox"
                              checked={!!editOverrides[key]}
                              onChange={e => setEditOverrides(p => ({ ...p, [key]: e.target.checked }))}
                              className={checkboxCls}
                            />
                            {label}
                          </label>
                        ))}
                      </div>

                      {/* ── Chess Title (shown when Titled is checked) ── */}
                      {editOverrides.titled && (
                        <div>
                          <label className={labelCls}>Chess Title</label>
                          <select
                            value={editOverrides.chessTitle || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, chessTitle: e.target.value }))}
                            className={inputCls}
                          >
                            <option value="">Select title...</option>
                            {['GM', 'IM', 'FM', 'CM', 'WGM', 'WIM', 'WFM', 'WCM', 'NM'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* ── Description ── */}
                      <div>
                        <label className={labelCls}>Description</label>
                        <textarea
                          value={editOverrides.description || ''}
                          onChange={e => setEditOverrides(p => ({ ...p, description: e.target.value }))}
                          rows="3"
                          className={`${inputCls} resize-none`}
                        />
                      </div>

                      {/* ── Long Description (HTML) ── */}
                      <div>
                        <label className={labelCls}>Long Description (HTML)</label>
                        <textarea
                          value={editOverrides.longDescription || ''}
                          onChange={e => setEditOverrides(p => ({ ...p, longDescription: e.target.value }))}
                          rows="6"
                          className={`${inputCls} resize-none font-mono`}
                          placeholder="<p>Rich description for the landing page...</p>"
                        />
                        {editOverrides.slug && (
                          <p className="text-gray-500 text-xs mt-1">Displayed on trendingchess.com/{editOverrides.slug}</p>
                        )}
                      </div>

                      {/* ── Features ── */}
                      <div>
                        <label className={labelCls}>Features (one per line)</label>
                        <textarea
                          value={editOverrides.featuresInput || ''}
                          onChange={e => setEditOverrides(p => ({ ...p, featuresInput: e.target.value }))}
                          rows="4"
                          className={`${inputCls} resize-none`}
                          placeholder={"Free and open source\nPuzzle training\nOnline tournaments"}
                        />
                        <p className="text-gray-500 text-xs mt-1">
                          {editOverrides.featuresInput ? editOverrides.featuresInput.split('\n').filter(f => f.trim()).length : 0} features
                        </p>
                      </div>

                      {/* ── Links Editor ── */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <label className={labelCls}>Social Links</label>
                          <button
                            type="button"
                            onClick={() => setLinksEntries(prev => [...prev, { key: '', value: '' }])}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs"
                          >
                            + Add Link
                          </button>
                        </div>
                        {linksEntries.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                            <input
                              type="text"
                              value={row.key}
                              onChange={e => setLinksEntries(prev => prev.map((r, i) => i === idx ? { ...r, key: e.target.value } : r))}
                              className={`col-span-3 ${inputCls}`}
                              placeholder="Platform"
                            />
                            <input
                              type="url"
                              value={row.value}
                              onChange={e => setLinksEntries(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
                              className={`col-span-8 ${inputCls}`}
                              placeholder="https://..."
                            />
                            <button
                              type="button"
                              onClick={() => setLinksEntries(prev => prev.filter((_, i) => i !== idx))}
                              className="col-span-1 bg-red-600 hover:bg-red-700 text-white px-2 py-2 rounded text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* ── Date + Comments ── */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>Date</label>
                          <input
                            type="date"
                            value={editOverrides.date || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, date: e.target.value }))}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Comments (internal)</label>
                          <input
                            type="text"
                            value={editOverrides.comments || ''}
                            onChange={e => setEditOverrides(p => ({ ...p, comments: e.target.value }))}
                            className={inputCls}
                            placeholder="Internal notes"
                          />
                        </div>
                      </div>

                      {/* ── Review Notes ── */}
                      <div>
                        <label className={labelCls}>Review Notes (optional)</label>
                        <input
                          type="text"
                          value={reviewNotes}
                          onChange={e => setReviewNotes(e.target.value)}
                          placeholder="Internal notes about this review"
                          className={inputCls}
                        />
                      </div>

                      {/* ── Action Buttons ── */}
                      <div className="flex gap-3 pt-3 border-t border-gray-700">
                        <button
                          onClick={() => handleAction(sub.id, 'reviewed')}
                          disabled={actionLoading === sub.id}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition-colors flex items-center gap-2 font-medium"
                        >
                          {actionLoading === sub.id && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                          {matchedWidget ? 'Save & Mark Reviewed' : 'Approve & Publish'}
                        </button>
                        <button
                          onClick={() => handleAction(sub.id, 'reject')}
                          disabled={actionLoading === sub.id}
                          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition-colors font-medium"
                        >
                          Remove from Site
                        </button>
                      </div>
                    </>
                  ) : (
                    /* Already reviewed — show review notes */
                    sub.reviewNotes && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Review Notes</label>
                        <p className="text-gray-300 text-sm">{sub.reviewNotes}</p>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubmissionReview;
